import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminGetAssistantConversations,
  adminGetAssistantConversationsByUser,
} from "./adminApi";
import "./AdminDashboard.css";

const INTENT_OPTIONS = [
  { label: "All", value: "" },
  { label: "Show Menu", value: "show_menu" },
  { label: "Product Info", value: "product_info" },
  { label: "Pricing", value: "pricing" },
  { label: "Demo Video", value: "demo_video" },
  { label: "Trial Request", value: "trial_request" },
  { label: "Route to Tool", value: "route_to_tool" },
  { label: "School Enquiry", value: "school_enquiry" },
  { label: "Tuition Enquiry", value: "tuition_enquiry" },
  { label: "Sales Lead", value: "sales_lead" },
  { label: "Support Issue", value: "support_issue" },
  { label: "Feedback", value: "feedback" },
  { label: "Complaint", value: "complaint" },
  { label: "Human Help", value: "human_help" },
  { label: "Invalid Input", value: "invalid_input" },
  { label: "Unknown", value: "unknown" },
];

const LIMIT_OPTIONS = ["50", "100", "250", "500"];

function displayValue(value) {
  return value === 0 || value === false || value ? String(value) : "-";
}

function formatBoolean(value) {
  if (value === true || value === "true" || value === "yes" || value === 1 || value === "1") {
    return "Yes";
  }
  if (value === false || value === "false" || value === "no" || value === 0 || value === "0") {
    return "No";
  }
  return "-";
}

function formatConfidence(value) {
  if (value === null || value === undefined || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return parsed.toFixed(2);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function getRowId(row, index) {
  return row?.RowKey || row?.row_key || row?.created_at || row?.timestamp || `conversation-${index}`;
}

function getSender(row) {
  return row?.sender_number || row?.whatsapp_number || "";
}

function getMessagePreview(message) {
  const text = String(message || "").replace(/\s+/g, " ").trim();
  if (!text) return "-";
  return text.length > 120 ? `${text.slice(0, 120)}...` : text;
}

function isLowConfidence(row) {
  const parsed = Number(row?.confidence);
  return Number.isFinite(parsed) && parsed < 0.5;
}

function getRows(payload) {
  const rows = payload?.rows || payload?.data?.rows || [];
  return Array.isArray(rows) ? rows : [];
}

function DetailBlock({ title, children }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontWeight: 700, color: "#111827" }}>{title}</div>
      <div
        style={{
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          lineHeight: 1.55,
          padding: 12,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {displayValue(children)}
      </div>
    </div>
  );
}

export default function AdminAssistantConversationsPage() {
  const navigate = useNavigate();
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [userId, setUserId] = useState("");
  const [intent, setIntent] = useState("");
  const [escalated, setEscalated] = useState("");
  const [limit, setLimit] = useState("100");
  const [lowConfidenceOnly, setLowConfidenceOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  function handleUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/admin/login", { replace: true });
      return true;
    }
    return false;
  }

  async function loadConversations(overrides = {}) {
    const nextFilters = {
      whatsappNumber,
      userId,
      intent,
      escalated,
      limit,
      ...overrides,
    };
    const normalizedLimit = (() => {
      const parsed = Number(nextFilters.limit);
      if (!Number.isInteger(parsed) || parsed <= 0) return 100;
      return Math.min(parsed, 500);
    })();

    setLoading(true);
    setError("");
    setExpandedRowId("");

    try {
      const trimmedUserId = nextFilters.userId.trim();
      const trimmedWhatsapp = nextFilters.whatsappNumber.trim();
      const hasUserFilter = Boolean(trimmedUserId || trimmedWhatsapp);
      const response = hasUserFilter
        ? await adminGetAssistantConversationsByUser({
            user_id: trimmedUserId,
            whatsapp_number: trimmedWhatsapp,
            limit: normalizedLimit,
          })
        : await adminGetAssistantConversations({
            limit: normalizedLimit,
            intent: nextFilters.intent.trim(),
            escalate: nextFilters.escalated,
          });

      setRows(getRows(response));
      setCount(response?.count ?? response?.data?.count ?? null);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setRows([]);
      setCount(null);
      setError(err.message || "Failed to load assistant conversations.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      if (lowConfidenceOnly && !isLowConfidence(row)) return false;
      if ((userId.trim() || whatsappNumber.trim()) && intent.trim() && row?.intent !== intent.trim()) return false;
      if (userId.trim() || whatsappNumber.trim()) {
        if (escalated === "true" && formatBoolean(row?.escalate) !== "Yes") return false;
        if (escalated === "false" && formatBoolean(row?.escalate) !== "No") return false;
      }
      return true;
    });
  }, [escalated, intent, lowConfidenceOnly, rows, userId, whatsappNumber]);

  function handleSubmit(event) {
    event.preventDefault();
    loadConversations();
  }

  function applyQuickFilter(type) {
    if (type === "escalated") {
      setEscalated("true");
      setLowConfidenceOnly(false);
      loadConversations({ escalated: "true" });
      return;
    }
    if (type === "sales") {
      setIntent("sales_lead");
      setLowConfidenceOnly(false);
      loadConversations({ intent: "sales_lead" });
      return;
    }
    if (type === "complaints") {
      setIntent("complaint");
      setLowConfidenceOnly(false);
      loadConversations({ intent: "complaint" });
      return;
    }
    if (type === "low-confidence") {
      setLowConfidenceOnly(true);
    }
  }

  function resetFilters() {
    setWhatsappNumber("");
    setUserId("");
    setIntent("");
    setEscalated("");
    setLimit("100");
    setLowConfidenceOnly(false);
    loadConversations({
      whatsappNumber: "",
      userId: "",
      intent: "",
      escalated: "",
      limit: "100",
    });
  }

  const tableHeaderStyle = {
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    padding: 8,
    verticalAlign: "top",
    whiteSpace: "nowrap",
  };
  const tableCellStyle = {
    borderBottom: "1px solid #f3f4f6",
    padding: 8,
    verticalAlign: "top",
  };

  return (
    <div className="admin-content">
      <div className="admin-card">
        <h2 className="admin-section-title">Assistant Conversations</h2>
        <form onSubmit={handleSubmit}>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>WhatsApp number</label>
              <input
                className="admin-input"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="+65..."
              />
            </div>
            <div className="admin-form-row">
              <label>User ID</label>
              <input
                className="admin-input"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="user_id"
              />
            </div>
            <div className="admin-form-row">
              <label>Intent</label>
              <select
                className="admin-input"
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
              >
                {INTENT_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-form-row">
              <label>Escalated</label>
              <select
                className="admin-input"
                value={escalated}
                onChange={(e) => setEscalated(e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="admin-form-row">
              <label>Limit</label>
              <select
                className="admin-input"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              >
                {LIMIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-button-row">
            <button type="submit" className="admin-button admin-button-primary" disabled={loading}>
              {loading ? "Loading..." : "Load Conversations"}
            </button>
            <button type="button" className="admin-button admin-button-secondary" onClick={resetFilters} disabled={loading}>
              Reset
            </button>
            <button type="button" className="admin-button admin-button-secondary" onClick={() => applyQuickFilter("escalated")} disabled={loading}>
              Escalated only
            </button>
            <button type="button" className="admin-button admin-button-secondary" onClick={() => applyQuickFilter("sales")} disabled={loading}>
              Sales leads
            </button>
            <button type="button" className="admin-button admin-button-secondary" onClick={() => applyQuickFilter("complaints")} disabled={loading}>
              Complaints
            </button>
            <button type="button" className="admin-button admin-button-secondary" onClick={() => applyQuickFilter("low-confidence")} disabled={loading}>
              Low confidence
            </button>
          </div>
        </form>

        {error ? <div className="admin-error" style={{ marginTop: 12 }}>{error}</div> : null}
        <div className="admin-meta" style={{ marginTop: 12 }}>
          {loading
            ? "Loading assistant conversations..."
            : `Showing ${visibleRows.length}${count !== null ? ` of ${count}` : ""} conversation${visibleRows.length === 1 ? "" : "s"}.`}
          {lowConfidenceOnly ? " Low confidence filter is active." : ""}
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="admin-meta">Loading assistant conversations...</div>
        ) : visibleRows.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  {[
                    "Time",
                    "WhatsApp / Sender",
                    "Intent",
                    "Product",
                    "Lead Type",
                    "Confidence",
                    "Escalated",
                    "Active Tool",
                    "User Message Preview",
                  ].map((heading) => (
                    <th key={heading} style={tableHeaderStyle}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, index) => {
                  const rowId = getRowId(row, index);
                  const expanded = expandedRowId === rowId;
                  return (
                    <React.Fragment key={rowId}>
                      <tr
                        onClick={() => setExpandedRowId(expanded ? "" : rowId)}
                        style={{ cursor: "pointer", background: expanded ? "#f9fafb" : "#ffffff" }}
                      >
                        <td style={tableCellStyle}>{formatDateTime(row.timestamp || row.created_at)}</td>
                        <td style={tableCellStyle}>{displayValue(getSender(row))}</td>
                        <td style={tableCellStyle}>{displayValue(row.intent)}</td>
                        <td style={tableCellStyle}>{displayValue(row.product)}</td>
                        <td style={tableCellStyle}>{displayValue(row.lead_type)}</td>
                        <td style={tableCellStyle}>{formatConfidence(row.confidence)}</td>
                        <td style={tableCellStyle}>{formatBoolean(row.escalate)}</td>
                        <td style={tableCellStyle}>{displayValue(row.active_tool)}</td>
                        <td style={{ ...tableCellStyle, minWidth: 260, lineHeight: 1.45 }}>
                          {getMessagePreview(row.incoming_message)}
                        </td>
                      </tr>
                      {expanded ? (
                        <tr>
                          <td colSpan={9} style={{ borderBottom: "1px solid #e5e7eb", padding: 12 }}>
                            <div style={{ display: "grid", gap: 14 }}>
                              <div className="admin-summary-grid">
                                <div className="admin-summary-item"><b>User ID:</b> {displayValue(row.user_id)}</div>
                                <div className="admin-summary-item"><b>User email:</b> {displayValue(row.user_email)}</div>
                                <div className="admin-summary-item"><b>WhatsApp number:</b> {displayValue(row.whatsapp_number || row.sender_number)}</div>
                                <div className="admin-summary-item"><b>Function name:</b> {displayValue(row.function_name)}</div>
                                <div className="admin-summary-item"><b>Message category:</b> {displayValue(row.message_category)}</div>
                                <div className="admin-summary-item"><b>RowKey:</b> {displayValue(row.RowKey)}</div>
                                <div className="admin-summary-item"><b>Created at:</b> {formatDateTime(row.created_at)}</div>
                              </div>
                              <DetailBlock title="Incoming message">{row.incoming_message}</DetailBlock>
                              <DetailBlock title="Assistant reply">{row.assistant_reply}</DetailBlock>
                              <DetailBlock title="Admin summary">{row.admin_summary}</DetailBlock>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-meta">No assistant conversations found.</div>
        )}
      </div>
    </div>
  );
}
